import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import 'reset-css'
import './index.css'
import {
    loadAPIToken,
    visorPROPipelineClient,
    visorPRORestClient,
} from '../../util/api'
import type { SelectChangeEvent } from '@mui/material'
import {
    Box,
    Drawer,
    drawerClasses,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Select,
    selectClasses,
    Stack,
    Typography,
} from '@mui/material'
import {
    Assistant,
    Build,
    Category,
    CorporateFare,
    DocumentScanner,
    Factory,
    Folder,
    Group,
    Newspaper,
    Science,
    Task,
} from '@mui/icons-material'
import { useEffectOnce } from 'react-use'
import { type APIUser } from '@visorpro/client'

export interface NavLinkFnProps {
    isActive: boolean
    isPending: boolean
}

const PR_PREVIEW_DOMAIN_REGEX = /^pr-(\d+)$/
const SIDEBAR_ITEMS = [
    [
        {
            title: 'Organizations',
            path: '/organizations',
            icon: <CorporateFare />,
        },
        { title: 'Users', path: '/users', icon: <Group /> },
    ],
    [
        { title: 'Data sets', path: '/data-sets', icon: <Folder /> },
        { title: 'Manufacturers', path: '/manufacturers', icon: <Factory /> },
        { title: 'Models', path: '/models', icon: <Category /> },
        { title: 'Documents', path: '/documents', icon: <DocumentScanner /> },
        {
            title: 'Knowledge articles',
            path: '/knowledge-articles',
            icon: <Newspaper />,
        },
        { title: 'Work orders', path: '/work-orders', icon: <Build /> },
    ],
    [
        { title: 'Prompts', path: '/prompts', icon: <Assistant /> },
        { title: 'Test runs', path: '/test-runs', icon: <Science /> },
        { title: 'Tasks', path: '/tasks', icon: <Task /> },
    ],
]

const Sidebar = ({ user }: { user: APIUser }) => {
    const DRAWER_WIDTH = 220

    return (
        <Drawer
            variant="permanent"
            sx={(theme) => ({
                display: { xs: 'none', md: 'unset' },
                width: DRAWER_WIDTH,
                flexShrink: 0,
                [`& .${drawerClasses.paper}`]: {
                    width: DRAWER_WIDTH,
                    backgroundColor: theme.palette.background.paper,
                    border: 0,
                },
            })}>
            <List sx={{ mt: '6px' }}>
                <DomainSelector />
            </List>
            {SIDEBAR_ITEMS.map((group, index) => (
                <List key={index}>
                    {group.map(({ title, path, icon }) => (
                        <NavLink
                            to={path}
                            style={{ color: 'inherit', textDecoration: 'none' }}
                            key={path}>
                            {({ isActive }) => (
                                <ListItem disablePadding key={path}>
                                    <ListItemButton selected={isActive}>
                                        {icon && (
                                            <ListItemIcon>{icon}</ListItemIcon>
                                        )}
                                        <ListItemText primary={title} />
                                    </ListItemButton>
                                </ListItem>
                            )}
                        </NavLink>
                    ))}
                </List>
            ))}
            <Box
                sx={{
                    overflow: 'auto',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}></Box>
            <CurrentUser user={user} />
        </Drawer>
    )
}

export const DomainSelector = () => {
    const [domain, setDomain] = useState('admin-dev')
    const [hasLocalhost, setHasLocalhost] = useState(false)

    const handleChange = (event: SelectChangeEvent) => {
        const env = event.target.value
        window.location.href = `http://${env}.visorpro.ai/`
    }

    useEffectOnce(() => {
        if (window.location.hostname.includes('localhost')) {
            setHasLocalhost(true)
            setDomain('admin-localhost')
        } else {
            const subdomain = window.location.hostname.split('.')[0]
            setDomain(subdomain)
        }
    })

    const prNumber = useMemo(() => {
        const match = domain.match(PR_PREVIEW_DOMAIN_REGEX)
        return match ? match[1] : null
    }, [domain])

    return (
        <Select
            labelId="company-select"
            id="company-simple-select"
            value={domain}
            onChange={handleChange}
            displayEmpty
            inputProps={{ 'aria-label': 'Select company' }}
            fullWidth
            sx={{
                maxHeight: 56,
                '&.MuiList-root': {
                    p: '8px',
                },
                [`& .${selectClasses.select}`]: {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2px',
                    pl: 1,
                },
            }}>
            {hasLocalhost && (
                <MenuItem value="admin-localhost">
                    <ListItemText primary="Localhost" secondary="Admin Panel" />
                </MenuItem>
            )}
            {prNumber && (
                <MenuItem value={domain}>
                    <ListItemText
                        primary={`PR Preview #${prNumber}`}
                        secondary="Admin Panel"
                    />
                </MenuItem>
            )}
            <MenuItem value="admin-dev">
                <ListItemText primary="Development" secondary="Admin Panel" />
            </MenuItem>
            <MenuItem value="admin-staging">
                <ListItemText primary="Staging" secondary="Admin Panel" />
            </MenuItem>
            <MenuItem value="admin">
                <ListItemText primary="Production" secondary="Admin Panel" />
            </MenuItem>
        </Select>
    )
}

export const CurrentUser = ({ user }: { user: APIUser }) => {
    return (
        <Stack
            direction="row"
            sx={{
                p: 2,
                gap: 1,
                alignItems: 'center',
                borderColor: 'divider',
            }}>
            <Box sx={{ mr: 'auto' }}>
                <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, lineHeight: '16px' }}>
                    {user?.first_name} {user?.last_name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {user?.email || user?.phone_number}
                </Typography>
            </Box>
        </Stack>
    )
}

export const RootPage: FC = () => {
    const navigate = useNavigate()
    const token = loadAPIToken()
    const [user, setUser] = useState<APIUser>()
    useEffectOnce(() => {
        try {
            if (!token) {
                navigate('/login')
                return
            }

            visorPRORestClient.setToken(token)
            visorPROPipelineClient.setToken(token)

            if (
                !visorPRORestClient.isAuthenticated ||
                !visorPROPipelineClient.isAuthenticated
            ) {
                navigate('/login')
                return
            }

            void visorPRORestClient.user.get().then((user) => {
                setUser(user)
            })
        } catch (e) {
            navigate('/login')
            return
        }
    })

    if (!user) return null

    return (
        <Stack direction="row">
            <Sidebar user={user} />
            <Box style={{ flex: 1, overflow: 'hidden' }}>
                <Outlet />
            </Box>
        </Stack>
    )
}
